import React, { useState, useEffect, CSSProperties } from 'react';
import styled from 'styled-components';
import { Button, Icon } from 'semantic-ui-react';
import { PDFViewerCore, PdfViewerProps } from '@/areas/settings/repository-management/components/file-viewing/PDFViewerCore';

const MobilePdfViewerContainer = styled.div`
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: #f7f8fb;
`;

const MobilePdfViewerContent = styled.div`
  flex: 1;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const NARROW_PAGINATION_BREAKPOINT = 520;

const MobileToolbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  background: #ffffff;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
`;

const ToolbarGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PageIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: #2f4f74;
  font-size: 0.95rem;

  @media (max-width: ${NARROW_PAGINATION_BREAKPOINT}px) and (orientation: portrait) {
    display: none;
  }
`;

const PaginationControl = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  .mobile-full-pagination {
    @media (max-width: ${NARROW_PAGINATION_BREAKPOINT}px) and (orientation: portrait) {
      display: none;
    }
  }
`;

export type MobilePDFViewerProps = PdfViewerProps & {
  style?: CSSProperties;
  isFullScreen?: boolean;
};

const MobilePDFViewer = ({ style, ...props }: MobilePDFViewerProps) => {
  const [pageNumber, setPageNumber] = useState(1);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [scale, setScale] = useState(props.scale ?? 1);
  const [viewMode, setViewMode] = useState<'scale' | 'fitWidth' | 'fullPage'>(props.viewMode ?? 'fitWidth');

  useEffect(() => {
    if (props.scale !== undefined) {
      setScale(props.scale);
    }
  }, [props.scale]);

  useEffect(() => {
    if (props.viewMode !== undefined) {
      setViewMode(props.viewMode);
    }
  }, [props.viewMode]);

  useEffect(() => {
    if (props.blob) {
      setPageNumber(1);
      setNumPages(null);
    }
  }, [props.blob]);

  const handleViewModeChange = (mode: 'scale' | 'fitWidth' | 'fullPage') => {
    setViewMode(mode);
    props.onViewModeChange?.(mode);
  };

  const handleScaleChange = (nextScale: number) => {
    setScale(nextScale);
    props.onScaleChange?.(nextScale);
  };

  const handlePageChange = (nextPage: number) => {
    setPageNumber(nextPage);
  };

  const handleDocumentPages = (documentPages: number) => {
    setNumPages(documentPages);
  };

  const toggleFitMode = () => {
    const nextMode = viewMode === 'fullPage' ? 'fitWidth' : 'fullPage';
    handleViewModeChange(nextMode);
    handleScaleChange(1);
  };

  return (
    <MobilePdfViewerContainer style={style}>
      <MobilePdfViewerContent>
        <PDFViewerCore
          blob={props.blob}
          pageNumber={pageNumber}
          scale={scale}
          viewMode={viewMode}
          onPageChange={handlePageChange}
          onScaleChange={handleScaleChange}
          onViewModeChange={handleViewModeChange}
          onNumPagesChange={handleDocumentPages}
          style={{ width: '100%', height: '100%', minWidth: 0, minHeight: 0 }}
        />
      </MobilePdfViewerContent>

      <MobileToolbar>
        <ToolbarGroup>
          <Button icon color="blue" onClick={() => handleScaleChange(Math.max(0.4, scale - 0.2))} title="Zoom out">
            <Icon name="search minus" />
          </Button>
          <Button icon color="green" onClick={() => handleScaleChange(Math.min(3, scale + 0.2))} title="Zoom in">
            <Icon name="search plus" />
          </Button>
          <Button icon onClick={toggleFitMode} title={viewMode === 'fullPage' ? 'Fit width' : 'Full page'}>
            <Icon name={viewMode === 'fullPage' ? 'arrows alternate horizontal' : 'window maximize'} />
          </Button>
        </ToolbarGroup>
        <PaginationControl>
          <Button icon className="mobile-full-pagination" disabled={pageNumber <= 1} onClick={() => setPageNumber(1)} title="First page">
            <Icon name="angle double left" />
          </Button>
          <Button icon disabled={pageNumber <= 1} onClick={() => setPageNumber(Math.max(1, pageNumber - 1))} title="Previous page">
            <Icon name="angle left" />
          </Button>
          <PageIndicator>
            <span>{pageNumber}</span>
            <span>of</span>
            <span>{numPages ?? '-'}</span>
          </PageIndicator>
          <Button icon disabled={!numPages || pageNumber >= numPages} onClick={() => numPages && setPageNumber(Math.min(numPages, pageNumber + 1))} title="Next page">
            <Icon name="angle right" />
          </Button>
          <Button icon className="mobile-full-pagination" disabled={!numPages || pageNumber >= numPages} onClick={() => numPages && setPageNumber(numPages)} title="Last page">
            <Icon name="angle double right" />
          </Button>
        </PaginationControl>
      </MobileToolbar>
    </MobilePdfViewerContainer>
  );
};

export default MobilePDFViewer;
